import React, { useContext, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { AssetTabs } from '../../components/assets/AssetTabs';
import { PortsTable } from '../../components/assets/PortsTable';
import { AssetsFilters } from '../../components/assets/AssetsFilters';
import { AssetsMetrics } from '../../components/assets/AssetsMetrics';
import { ExportButton, AddAssetButton } from '../../components/assets/AssetActionButtons';
import { useTeams } from '../../contexts/TeamsContext';
import { AssetsApi, Port } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { AuthContext } from '../../contexts/AuthContext';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import ExportActionBtn from '@/components/ui/ExportActionBtn';


const Ports = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize state from URL search params
  const [currentPage, setCurrentPage] = React.useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('search') || '');
  const [selectedType, setSelectedType] = React.useState(searchParams.get('type') || 'all');
  const [selectedStatus, setSelectedStatus] = React.useState(searchParams.get('status') || 'all');
  const [selectedTags, setSelectedTags] = React.useState<string[]>(
    searchParams.getAll('tag') || []
  );
  const [selectedRegions, setSelectedRegions] = React.useState<string[]>(
    searchParams.getAll('region') || []
  );
  const [selectedOrdering, setSelectedOrdering] = React.useState<string>(searchParams.get('ordering') || 'riskScore');
  const [ports, setPorts] = React.useState<Port[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = React.useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });

  // Update local state when search parameters change (for back button navigation)
  useEffect(() => {
    // Only update if the change is from navigation (not from local state changes)
    // This prevents infinite loops
    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const typeParam = searchParams.get('type');
    const statusParam = searchParams.get('status');
    const tagParams = searchParams.getAll('tag');
    const regionParams = searchParams.getAll('region');
    const orderingParam = searchParams.get('ordering');
    
    // Update page if it changed
    if (pageParam) {
      const newPage = parseInt(pageParam, 10);
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    } else if (currentPage !== 1) {
      // If page param is removed, reset to page 1
      setCurrentPage(1);
    }
    
    // Update search query if it changed
    if (searchParam !== searchQuery) {
      setSearchQuery(searchParam || '');
    }
    
    // Update type if it changed
    if (typeParam !== selectedType) {
      setSelectedType(typeParam || 'all');
    }
    
    // Update status if it changed
    if (statusParam !== selectedStatus) {
      setSelectedStatus(statusParam || 'all');
    }
    
    // Update tags if they changed
    if (JSON.stringify(tagParams) !== JSON.stringify(selectedTags)) {
      setSelectedTags(tagParams || []);
    }
    
    // Update regions if they changed
    if (JSON.stringify(regionParams) !== JSON.stringify(selectedRegions)) {
      setSelectedRegions(regionParams || []);
    }
    
    // Update ordering if it changed
    if (orderingParam !== selectedOrdering) {
      setSelectedOrdering(orderingParam || 'riskScore');
    }
  }, [searchParams]);

  const fetchPorts = React.useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new AssetsApi(getApiConfiguration(token));
      const response = await api.portsList({
        teamSlug: currentTeam.slug,
        page: currentPage,
        search: searchQuery,
        ordering: selectedOrdering
      });
      setPorts(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching ports:', err);
      setError('Failed to load ports');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery, selectedOrdering]);

  const fetchAllPorts = async (limit: number) => {
    if (!currentTeam?.slug || !token) return [];
    
    const api = new AssetsApi(getApiConfiguration(token));
    const response = await api.portsList({
      teamSlug: currentTeam.slug,
      search: searchQuery,
      ordering: selectedOrdering,
      limit
    });
    return response.results;
  };

  React.useEffect(() => {
    fetchPorts();
  }, [fetchPorts]);

  // Helper function to build URL with search parameters
  const buildUrlWithParams = (params: Record<string, any>) => {
    const urlParams = new URLSearchParams();
    
    // Add page parameter if greater than 1
    if (params.page && params.page > 1) {
      urlParams.set('page', params.page.toString());
    }
    
    // Add search query if present
    if (params.search) {
      urlParams.set('search', params.search);
    }
    
    // Add type if not 'all'
    if (params.type && params.type !== 'all') {
      urlParams.set('type', params.type);
    }
    
    // Add status if not 'all'
    if (params.status && params.status !== 'all') {
      urlParams.set('status', params.status);
    }
    
    // Add tags if present
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((tag: string) => urlParams.append('tag', tag));
    }
    
    // Add regions if present
    if (params.regions && params.regions.length > 0) {
      params.regions.forEach((region: string) => urlParams.append('region', region));
    }
    
    // Add ordering if present
    if (params.ordering) {
      urlParams.set('ordering', params.ordering);
    }
    
    return `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  };

  // Handle page change with URL param update
  const handlePageChange = (page: number) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page,
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { pageChange: true, timestamp: Date.now() }
    });
    
    // Update the local state
    setCurrentPage(page);
  };

  // Handle search change with URL param update
  const handleSearchChange = (query: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on search
      search: query,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { searchChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Handle type change
  const handleTypeChange = (type: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedType(type);
    setCurrentPage(1);
  };

  // Handle status change
  const handleStatusChange = (status: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status,
      tags: selectedTags,
      regions: selectedRegions,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  // Handle tag change
  const handleTagChange = (tag: string, checked: boolean) => {
    const newTags = checked 
      ? [...selectedTags, tag] 
      : selectedTags.filter(t => t !== tag);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: newTags,
      regions: selectedRegions,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedTags(newTags);
    setCurrentPage(1);
  };

  // Handle region change
  const handleRegionChange = (region: string, checked: boolean) => {
    const newRegions = checked 
      ? [...selectedRegions, region] 
      : selectedRegions.filter(r => r !== region);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: newRegions,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedRegions(newRegions);
    setCurrentPage(1);
  };

  // Handle ordering change
  const handleOrderingChange = (value: string) => {
    setSelectedOrdering(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      ordering: value
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Metrics for domains page - using count from API response
  const portMetrics = {
    web: ports.length,
    servers: 0,
    databases: 0,
    network: 0,
    vulnerable: ports.length,
    secure: ports.length,
    total: ports.length
  };

  // Handle filter reset
  const handleReset = () => {
    // Navigate to base URL without parameters
    navigate(location.pathname, {
      state: { reset: true, timestamp: Date.now() }
    });
    
    // Reset all local state
    setSearchQuery('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedTags([]);
    setSelectedRegions([]);
    setSelectedOrdering('riskScore');
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between"> 
        <div className="flex items-center space-x-3">
          <Globe className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Ports</h1>
            <p className="text-sm text-gray-400">Manage and monitor your ports </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <ExportActionBtn
            fetchAllData={() => fetchAllPorts(paginationInfo.count)}
            filename="ports"
            disabled={ports?.length === 0}
          />
          <AddAssetButton onClick={() => console.log('Adding new port...')} />
        </div>
      </div>

      {/* Metrics Overview 
      <AssetsMetrics metrics={portMetrics} />
*/}
      {/* Tabs */}
      <AssetTabs />

      {/* Filters */}
      <AssetsFilters
        assetType="ports"
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        selectedType={selectedType}
        onTypeChange={handleTypeChange}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedTags={selectedTags}
        onTagChange={handleTagChange}
        selectedRegions={selectedRegions}
        onRegionChange={handleRegionChange}
        selectedOrdering={selectedOrdering}
        onOrderingChange={handleOrderingChange}
        onReset={handleReset}
        onExport={() => {
          console.log('Exporting ports...');
        }}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading ports</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <PortsTable
          ports={ports}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          totalPorts={paginationInfo.count}
          itemsPerPage={10}
          paginationInfo={paginationInfo}
        />
      )}
    </div>
  );
};

export default Ports;